import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Sparkles, Info, Loader2 } from "lucide-react";
import {  PillChip, SwitchToggle, PrimaryButton  } from "@/components/toggles";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ModulGListingPreviewConfigPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { listingId } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [listing, setListing] = useState(null);
  const [activityCatalog, setActivityCatalog] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [articleType, setArticleType] = useState("");
  const [wearMin, setWearMin] = useState(1);
  const [wearMax, setWearMax] = useState(7);
  const [activities, setActivities] = useState([]);
  const [articlePhotos, setArticlePhotos] = useState([]);
  const [consentNote, setConsentNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [lst, acts] = await Promise.all([
        api.get(`/listings/${listingId}`),
        api.get("/modul-g/activity-presets"),
      ]);
      setListing(lst.data.listing || lst.data);
      const listingData = (lst.data.listing || lst.data);
      const cfg = listingData.preview_config || {};
      setEnabled(!!cfg.try_on_enabled);
      // Article-Type wird automatisch aus listing.category_code abgeleitet (Owner-Vorgabe)
      setArticleType(cfg.article_type || listingData.category_code || "");
      setWearMin(cfg.wear_duration_days?.min ?? 1);
      setWearMax(cfg.wear_duration_days?.max ?? 7);
      setActivities(cfg.activity_presets || []);
      // Auto-Vorwahl: wenn noch nichts gewählt und mind. 1 Foto vorhanden → erstes wählen
      const initialPhotos = cfg.article_photo_urls || [];
      if (initialPhotos.length === 0 && listingData.media_urls?.length) {
        setArticlePhotos([listingData.media_urls[0]]);
      } else {
        setArticlePhotos(initialPhotos);
      }
      setConsentNote(cfg.consent_note || "");
      setActivityCatalog(acts.data.presets || []);
    } catch (e) {
      setError(e.response?.data?.detail?.message || "Fehler beim Laden");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) { navigate(`/login?next=/studio/listings/${listingId}/preview-config`); return; }
    load();
  }, [user, listingId]);

  const toggleActivity = (code) => {
    setActivities((cur) => cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]);
  };
  const togglePhoto = (url) => {
    setArticlePhotos((cur) => cur.includes(url) ? cur.filter((u) => u !== url) : [...cur, url]);
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.patch(`/listings/${listingId}/preview-config`, {
        try_on_enabled: enabled,
        article_type: articleType.trim().toLowerCase() || null,
        article_photo_urls: articlePhotos,
        wear_duration_days: { min: parseInt(wearMin, 10), max: parseInt(wearMax, 10) },
        activity_presets: activities,
        consent_note: consentNote.trim() || null,
      });
      navigate(-1);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(d?.message || d || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const resetCache = async () => {
    if (!window.confirm(t("modul_g_config.confirm_reset_cache"))) return;
    setSaving(true); setError("");
    try {
      const res = await api.post(`/listings/${listingId}/preview-config/reset-cache`);
      const n = res.data?.invalidated ?? 0;
      alert(`Fertig – ${n} alte Vorschau(en) verworfen. Beim nächsten Try-On erhalten Käufer frische KI-Bilder.`);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(d?.message || d || "Cache-Reset fehlgeschlagen");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" data-testid="mg-lpc-loading"><Loader2 className="animate-spin text-[#666]" /></div>;

  const media = listing?.media_urls || [];

  return (
    <div data-testid="mg-lpc-page" className="min-h-screen bg-white pb-24">
      <div className="sticky top-0 z-40 flex items-center gap-3 h-14 px-4 border-b border-[#E5E5E5] bg-white">
        <button onClick={() => navigate(-1)} className="text-[#0A0A0A]" data-testid="mg-lpc-back"><ArrowLeft size={20} /></button>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-[#999]">Modul G · Try-On-Konfiguration</p>
          <h1 className="text-base font-bold truncate">{listing?.title || "Listing"}</h1>
        </div>
        <PrimaryButton variant="primary" size="sm" testId="mg-lpc-save-header-btn" onClick={save} disabled>{saving ? "…" : "Speichern"}</PrimaryButton>
      </div>

      <div className="p-4 space-y-5">
        {error && <div className="border border-red-400 bg-red-50 text-red-700 text-xs p-3" data-testid="mg-lpc-error">{error}</div>}

        {/* Master-Toggle */}
        <label className="flex items-start gap-3 border border-[#E5E5E5] p-3 cursor-pointer" data-testid="mg-lpc-toggle-row">
          <SwitchToggle
            testId="mg-lpc-enable-toggle"
            checked={enabled}
            onChange={() => setEnabled((v) => !v)}
            variant="neutral"
            className="mt-0.5"
            ariaLabel={t("modul_g_config.enable_try_on")}
          />
          <div className="flex-1">
            <p className="text-sm font-semibold flex items-center gap-1.5"><Sparkles size={13} /> {t("modul_g_config.enable_try_on")}</p>
            <p className="text-[11px] text-[#666] mt-1">K&auml;ufer sehen im Artikel eine &bdquo;Virtuell anprobieren&ldquo;-Karte und k&ouml;nnen Vorschauen mit Tragedauer &amp; Aktivit&auml;t anfordern.</p>
          </div>
        </label>

        {enabled && (
          <>
            {/* Artikel-Typ wird automatisch aus Listing.category_code abgeleitet (Owner-Vorgabe 2026-07-06).
                Kein manuelles Feld mehr. */}

            {/* Artikel-Fotos-Auswahl */}
            {media.length > 0 && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#999]">{t("modul_g_config.photos_for_try_on")}</label>
                <p className="text-[10px] text-[#999] mt-0.5">{t("modul_g_config.photos_hint")}</p>
                <div className="grid grid-cols-3 gap-1.5 mt-2">
                  {media.map((url) => {
                    const on = articlePhotos.includes(url);
                    return (
                      <button
                        key={url}
                        data-testid={`mg-lpc-photo-${on ? "on" : "off"}`}
                        onClick={() => togglePhoto(url)}
                        className={`relative border aspect-square overflow-hidden ${on ? "border-[#0A0A0A] ring-2 ring-[#0A0A0A]" : "border-[#E5E5E5]"}`}>
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        {on && <span className="absolute top-1 right-1 bg-[#0A0A0A] text-white text-[9px] px-1.5 py-0.5 rounded">✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tragedauer-Range */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#999]">{t("modul_g_config.wear_days")}</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <input data-testid="mg-lpc-wear-min" type="number" min="0" max="90" value={wearMin} onChange={(e) => setWearMin(e.target.value)} className="border border-[#E5E5E5] px-3 py-2 text-sm" />
                <input data-testid="mg-lpc-wear-max" type="number" min="1" max="90" value={wearMax} onChange={(e) => setWearMax(e.target.value)} className="border border-[#E5E5E5] px-3 py-2 text-sm" />
              </div>
              <p className="text-[10px] text-[#666] mt-1">{t("modul_g_config.wear_days_hint")}</p>
            </div>

            {/* Aktivitäts-Presets */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#999]">{t("modul_g_config.unlock_activities")}</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {activityCatalog.map((a) => {
                  const on = activities.includes(a.code);
                  return (
                    <PillChip
                      key={a.code}
                      testId={`mg-lpc-activity-${a.code}`}
                      active={on}
                      onClick={() => toggleActivity(a.code)}
                      variant="neutral"
                      size="md"
                    >
                      {a.label}
                    </PillChip>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#666] mt-1">{t("modul_g_config.activities_hint")}</p>
            </div>

            {/* Consent-Hinweis */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#999]">{t("modul_g_config.buyer_note")}</label>
              <textarea
                data-testid="mg-lpc-consent-note"
                value={consentNote}
                onChange={(e) => setConsentNote(e.target.value)}
                maxLength={400}
                rows={3}
                placeholder={t("modul_g_config.note_placeholder")}
                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm mt-1"
              />
            </div>

            <div className="border-l-2 border-amber-400 bg-amber-50 p-3 flex gap-2" data-testid="mg-lpc-info">
              <Info size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-800">Käufer sehen ausschließlich anonymisierte KI-Vorschauen mit Wasserzeichen. Die Anonymisierungs-Einstellungen deines Motivs (Tattoos entfernen, Gesicht unkenntlich, …) gelten automatisch.</p>
            </div>

            <button
              type="button"
              data-testid="mg-lpc-reset-cache-btn"
              onClick={resetCache}
              disabled={saving}
              className="w-full h-10 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium disabled:opacity-40">
              KI-Vorschauen dieses Artikels neu rendern (Cache leeren)
            </button>
            <p className="text-[10px] text-[#999] -mt-3 text-center">{t("modul_g_config.reset_hint")}</p>
          </>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-[#E5E5E5] bg-white p-3 pb-[calc(env(safe-area-inset-bottom)+12px)] z-40">
        <PrimaryButton variant="primary" size="md" testId="mg-lpc-save-btn" onClick={save} disabled>{saving ? "Wird gespeichert…" : "Speichern"}</PrimaryButton>
      </div>
      {/* Spacer für den fixierten Save-Button, damit letzter Content nicht verdeckt wird */}
      <div className="h-20" aria-hidden="true" />
    </div>
  );
}
